import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Check, Chrome, Cloud, Laptop, UserRound, LifeBuoy, Moon, RefreshCw, Sparkles } from 'lucide-react';
import { RiWhatsappFill } from 'react-icons/ri';
import { CHROME_WEB_STORE_URL, namespace } from '@common-config/constants';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';
import { ConnectGatewayDialog } from '../engines-management/components/ConnectGatewayDialog';
import type { DisplayEngine } from '../engines-management/types';
import type { LocalSubstate, LocalSupport } from './onboarding-logic';
import type { TrialState } from './use-connect-flow';
import { C, GhostButton, Heading, Notice, OptionRow, PathStep, PrimaryButton, StepState, WaitingChip } from './onboarding-ui';

const CHROME_DOWNLOAD_URL = 'https://www.google.com/chrome/';

const useOnb = () => {
  const { t } = useTranslation(namespace);
  return (key: string, opts?: Record<string, unknown>) => t(`onboarding.${key}`, opts);
};

/* ────────────────────────── 1. One way to start ────────────────────────── */

/** The one recommended way to connect from THIS device — the page's one solid button. */
const Choice: React.FC<{ icon: React.ReactNode; title: string; tag: string; body: string; cta: string; onClick: () => void }> = ({ icon, title, tag, body, cta, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="onb-choice"
    style={{
      display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'start', padding: 24, borderRadius: 18, border: `1px solid ${C.border}`,
      background: C.surface, cursor: 'pointer', fontFamily: 'inherit', color: C.text, width: '100%',
    }}
  >
    <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span aria-hidden style={{ width: 46, height: 46, borderRadius: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.inkSoft, color: C.ink }}>
        {icon}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 19, fontWeight: 700, letterSpacing: '-.01em' }}>{title}</span>
        <span style={{ alignSelf: 'flex-start', fontSize: 12, fontWeight: 750, padding: '2px 9px', borderRadius: 999, background: C.track, color: C.muted }}>{tag}</span>
      </span>
    </span>
    <span style={{ fontSize: 15, lineHeight: 1.55, color: C.muted }}>{body}</span>
    <span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 8, height: 44, padding: '0 20px', borderRadius: 11, background: C.brand, color: C.brandText, fontSize: 15, fontWeight: 650 }}>
      {cta}<ArrowRight size={16} strokeWidth={2.5} className="bt-arrow" />
    </span>
  </button>
);

export const ChooseStep: React.FC<{
  /** What this device can run (localSupport). */
  device: LocalSupport;
  proPrice: string | null;
  onChoose: (path: 'local' | 'remote') => void;
  onShareLink: () => Promise<boolean>;
  onVideo: () => void;
}> = ({ device, proPrice, onChoose, onShareLink, onVideo }) => {
  const o = useOnb();
  const [copied, setCopied] = useState(false);
  const shareLink = () => { onShareLink().then(ok => { if (ok) { setCopied(true); window.setTimeout(() => setCopied(false), 2500); } }); };
  return (
    <div className="onb-in" style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
      <Heading title={o('choose.singleTitle')} body={device === 'phone' ? undefined : o('choose.singleBody')} center />
      {/* One way that works on THIS device, the other one quietly underneath. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {device !== 'phone' ? (
          <>
            {device === 'chrome' ? (
              <Choice icon={<Laptop size={24} />} title={o('choose.localTitle')} tag={o('choose.free')} body={o('choose.localBody')} cta={o('choose.localCta')} onClick={() => onChoose('local')} />
            ) : (
              // A computer in Safari / Firefox: the free path is still the one — it just runs in Chrome.
              <Choice icon={copied ? <Check size={24} /> : <Chrome size={24} />} title={o('choose.localTitle')} tag={o('choose.free')} body={o('choose.inChromeBody')} cta={copied ? o('choose.inChromeCopied') : o('choose.inChromeCta')} onClick={shareLink} />
            )}
            <OptionRow icon={<Moon size={18} />} title={o('choose.offTitle')} badge={o('choose.pro')} body={o('choose.offBody')} cta={o('choose.offCta')} onClick={() => onChoose('remote')} />
          </>
        ) : (
          <>
            <Choice icon={<Cloud size={24} />} title={o('choose.remoteTitle')} tag={proPrice ? o('choose.proPrice', { price: proPrice }) : o('choose.pro')} body={o('choose.remoteBody')} cta={o('choose.remoteCta')} onClick={() => onChoose('remote')} />
            <OptionRow icon={copied ? <Check size={18} /> : <Laptop size={18} />} title={o('choose.localTitle')} badge={o('choose.free')} body={o('choose.localNeedsChrome')} cta={copied ? o('choose.linkCopied') : o('choose.copyLink')} onClick={shareLink} />
          </>
        )}
      </div>
      {device === 'otherBrowser' && (
        <p style={{ margin: '-14px 0 0', textAlign: 'center', fontSize: 14, color: C.muted }}>
          {o('choose.noChrome')}{' '}
          <a className="onb-link" href={CHROME_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">{o('choose.getChrome')}</a>
        </p>
      )}
      {/* Lesson 1.2 (16:9, YouTube) — the same video as everywhere else for this topic. */}
      <p style={{ margin: 0, textAlign: 'center', fontSize: 14.5, color: C.muted }}>
        <GuideVideoButton lesson="connect-whatsapp" variant="link" label={o('choose.watch')} surface="onboarding_choose" onPlay={onVideo} style={{ fontSize: 14.5 }} />
      </p>
    </div>
  );
};

/* ────────────────────────── 2a. On this computer ────────────────────────── */

export const LocalStep: React.FC<{
  /** Where the connection is stuck, read from the extension's ping (see localSubstate). */
  substate: LocalSubstate;
  /** An older extension can't report its account — after a while, offer the account fix anyway. */
  canTellAccount: boolean;
  email: string;
  /** Handing this account to the extension: in flight, or no answer (→ the by-hand way). */
  link: 'idle' | 'linking' | 'failed';
  onLinkAccount: () => void;
  onGoToWhatsApp: () => void;
  onBack: () => void;
  onTrack: (name: string, props?: Record<string, unknown>) => void;
}> = ({ substate, canTellAccount, email, link, onLinkAccount, onGoToWhatsApp, onBack, onTrack }) => {
  const o = useOnb();
  const [addClickedAt, setAddClickedAt] = useState<number | null>(null);
  // Hints only after a fair wait — never nag someone who is mid-install or mid-scan.
  const [now, setNow] = useState(Date.now());
  const [linkingSince, setLinkingSince] = useState<number | null>(null);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => { setLinkingSince(substate === 'linking' ? Date.now() : null); }, [substate]);

  const installed = substate !== 'install';
  const accountIssue = substate === 'otherAccount' || substate === 'signedOut';
  const waOpen = substate === 'linking';
  const extensionSlow = !installed && addClickedAt !== null && now - addClickedAt > 60_000;
  const linkingSlow = waOpen && linkingSince !== null && now - linkingSince > 90_000;
  // An older extension can't say which account it uses: if WhatsApp Web has been open a while
  // without connecting, the likely cause is a different account — say so.
  const offerAccountFix = waOpen && !canTellAccount && linkingSince !== null && now - linkingSince > 25_000;
  useEffect(() => { if (extensionSlow) onTrack('onboarding_stuck', { step: 'extension' }); }, [extensionSlow]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (linkingSlow) onTrack('onboarding_stuck', { step: 'whatsapp_web' }); }, [linkingSlow]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (accountIssue) onTrack('onboarding_extension_account', { state: substate }); }, [substate]); // eslint-disable-line react-hooks/exhaustive-deps

  const step1: StepState = installed ? 'done' : 'current';
  const step2: StepState = waOpen ? 'done' : installed ? 'current' : 'todo';
  const step3: StepState = waOpen ? 'current' : 'todo';

  // Step 2 — the extension must use THIS account before WhatsApp Web is worth opening. The web app
  // hands its own sign-in to the extension in one click; no signing out and in by hand.
  let step2Title = o(waOpen ? 'local.step2Done' : 'local.step2Title');
  let step2Body: React.ReactNode = o(waOpen ? 'local.step2DoneBody' : 'local.step2Body');
  let step2Action: React.ReactNode = step2 === 'current' ? (
    <PrimaryButton
      tone="whatsapp"
      icon={<RiWhatsappFill size={20} aria-hidden />}
      onClick={() => {
        onTrack('onboarding_whatsapp_web_clicked');
        window.open('https://web.whatsapp.com', '_blank', 'noopener,noreferrer');
      }}
    >
      {o('local.step2Cta')}
    </PrimaryButton>
  ) : undefined;
  if (accountIssue) {
    const other = substate === 'otherAccount';
    step2Title = o(other ? 'local.otherAccountTitle' : 'local.signedOutTitle');
    step2Body = <>{o(other ? 'local.linkOtherBody' : 'local.linkSignedOutBody')} <strong dir="ltr" style={{ color: C.text, fontWeight: 650 }}>{email}</strong></>;
    step2Action = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 14 }}>
        <PrimaryButton icon={<UserRound size={18} />} busy={link === 'linking'} onClick={onLinkAccount}>{o('local.linkCta')}</PrimaryButton>
        {link === 'linking' && <WaitingChip label={o('local.linkWaiting')} />}
        {link === 'failed' && (
          <Notice icon={<LifeBuoy size={16} />} action={<button type="button" className="onb-link" onClick={onGoToWhatsApp}>{o('local.goToWhatsApp')}</button>}>
            {o('local.linkFailed')}
          </Notice>
        )}
      </div>
    );
  }

  const step3Action = waOpen ? (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 14 }}>
      <WaitingChip label={o('local.waiting')} />
      {/* Always say which account — the one thing that silently blocks this step. */}
      <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13.5, color: C.muted }}>
        <UserRound size={14} aria-hidden style={{ flex: '0 0 auto' }} />
        <span>{o('local.signedInAs')} <strong dir="ltr" style={{ color: C.text, fontWeight: 650 }}>{email}</strong></span>
        {offerAccountFix && <button type="button" className="onb-link" style={{ fontSize: 13.5 }} onClick={onGoToWhatsApp}>{o('local.goToWhatsApp')}</button>}
      </p>
      {linkingSlow && <Notice icon={<LifeBuoy size={16} />}>{o('local.waSlow')}</Notice>}
    </div>
  ) : undefined;

  return (
    <div className="onb-in" style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <Heading title={o('local.title')} body={o('local.body')} />
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        <PathStep
          n={1}
          state={step1}
          doneLabel={o('stepDone')}
          title={o(installed ? 'local.step1Done' : 'local.step1Title')}
          body={o(installed ? 'local.step1DoneBody' : 'local.step1Body')}
          action={step1 === 'current' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 14 }}>
              <PrimaryButton
                icon={<Chrome size={18} />}
                onClick={() => {
                  onTrack('onboarding_extension_clicked');
                  setAddClickedAt(Date.now());
                  window.open(CHROME_WEB_STORE_URL, '_blank', 'noopener,noreferrer');
                }}
              >
                {o('local.step1Cta')}
              </PrimaryButton>
              {extensionSlow && (
                <Notice icon={<RefreshCw size={16} />} action={<button type="button" className="onb-link" onClick={() => window.location.reload()}>{o('local.refresh')}</button>}>
                  {o('local.extensionSlow')}
                </Notice>
              )}
            </div>
          ) : undefined}
        />
        <PathStep n={2} state={step2} doneLabel={o('stepDone')} title={step2Title} body={step2Body} action={step2Action} />
        <PathStep
          n={3}
          last
          state={step3}
          title={o(waOpen ? 'local.linkingTitle' : 'local.step3Title')}
          body={o(waOpen ? 'local.linkingBody' : 'local.step3Body')}
          action={step3Action}
        />
      </ol>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <button type="button" className="onb-link" onClick={onBack} style={{ color: C.muted, fontWeight: 600 }}>
          <ArrowLeft size={15} className="bt-arrow" />{o('back')}
        </button>
        {/* Lesson 1.3 — renders nothing until the video is published. */}
        <GuideVideoButton lesson="whatsapp-web-extension" variant="link" surface="onboarding_local" />
      </div>
    </div>
  );
};

/* ────────────────────────── 2b. Always on ────────────────────────── */

export const RemoteStep: React.FC<{
  loading: boolean;
  isPro: boolean;
  proPrice: string | null;
  supportsLocal: boolean;
  engine: DisplayEngine | undefined;
  expired: boolean;
  isCreating: boolean;
  /** Pro, nothing waiting to link, and every always-on slot already used. */
  atLimit: boolean;
  /** R1 — a free trial first; the paid plan only once this workspace already used its trial. */
  trial: TrialState;
  onStartTrial: () => void;
  onManage: () => void;
  onCreate: () => void;
  onUpgrade: () => void;
  onUseLocal: () => void;
  onBack: () => void;
}> = ({ loading, isPro, proPrice, supportsLocal, engine, expired, isCreating, atLimit, trial, onStartTrial, onManage, onCreate, onUpgrade, onUseLocal, onBack }) => {
  const o = useOnb();
  const back = (
    <div>
      <button type="button" className="onb-link" onClick={onBack} style={{ color: C.muted, fontWeight: 600 }}>
        <ArrowLeft size={15} className="bt-arrow" />{o('back')}
      </button>
    </div>
  );

  if (loading) {
    return <div style={{ height: 260 }} aria-busy />;
  }

  if (!isPro) {
    // No trial on offer (already had one, or billing couldn't be checked): the paid plan instead.
    const paidOnly = trial === 'used' || trial === 'unavailable';
    const notice = trial === 'used' ? 'remote.trialUsed' : trial === 'unavailable' ? 'remote.trialUnavailable' : trial === 'error' ? 'remote.trialError' : null;
    return (
      <div className="onb-in" style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
        {/* A free trial first — the paid plan only once the trial isn't available. */}
        <Heading title={o(paidOnly ? 'remote.upsellTitle' : 'remote.trialTitle')} body={o(paidOnly ? 'remote.upsellBody' : 'remote.trialBody')} />
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {['remote.benefit1', 'remote.benefit2', 'remote.benefit3'].map((k) => (
            <li key={k} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 15.5, color: C.text }}>
              <span aria-hidden style={{ width: 26, height: 26, borderRadius: 999, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.inkSoft, color: C.ink }}>
                <Check size={15} strokeWidth={3} />
              </span>
              {o(k)}
            </li>
          ))}
        </ul>
        {notice && <Notice icon={<Sparkles size={16} />}>{o(notice)}</Notice>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {paidOnly ? (
            <PrimaryButton icon={<Cloud size={18} />} onClick={onUpgrade}>
              {proPrice ? o('remote.upgradeCtaPrice', { price: proPrice }) : o('remote.upgradeCta')}
            </PrimaryButton>
          ) : (
            <PrimaryButton icon={<Sparkles size={18} />} busy={trial === 'checking' || trial === 'busy' || trial === 'unlocking'} onClick={onStartTrial}>{o('remote.trialCta')}</PrimaryButton>
          )}
          {supportsLocal && <GhostButton icon={<Laptop size={17} />} onClick={onUseLocal}>{o('remote.useLocal')}</GhostButton>}
        </div>
        <p style={{ margin: 0, fontSize: 14, color: C.muted }}>{o(paidOnly ? 'remote.switchAnyTime' : 'remote.trialNote')}</p>
        {!supportsLocal && back}
      </div>
    );
  }

  if (!engine && atLimit) {
    return (
      <div className="onb-in" style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
        <Heading title={o('remote.limitTitle')} body={o('remote.limitBody')} />
        <div><PrimaryButton icon={<Cloud size={18} />} onClick={onManage}>{o('remote.limitCta')}</PrimaryButton></div>
        {back}
      </div>
    );
  }

  if (!engine) {
    return (
      <div className="onb-in" style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
        <Heading title={o('remote.createTitle')} body={<>{o('remote.createBody')}<br /><strong style={{ color: C.text, fontWeight: 600 }}>{o('remote.phoneReady')}</strong></>} />
        <div><PrimaryButton icon={<Cloud size={18} />} busy={isCreating} onClick={onCreate}>{o('remote.createCta')}</PrimaryButton></div>
        {back}
      </div>
    );
  }

  return (
    <div className="onb-in" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Heading title={o('remote.linkTitle')} body={o('remote.linkBody')} />
      <div style={{ maxWidth: 440, width: '100%', alignSelf: 'center' }}>
        <ConnectGatewayDialog inline open onOpenChange={() => undefined} engine={engine} expired={expired} onStartOver={onCreate} />
      </div>
    </div>
  );
};
